/**
 * The demo's 2-step intro (SKE-4234).
 *
 * Teaches the two-pane in seconds: it dims one side and names the other —
 * "this is your client's world" / "and this is you, watching it land" — then
 * hands off with "approve one". Auto-plays once per visitor (localStorage),
 * fully skippable, and only ever mounted on the desktop two-pane (Homepage
 * gates it on `isDemoMode() && !isMobile`).
 *
 * PORTAL / FIXED CHROME
 * ---------------------
 * Rendered into `document.body`, so it is a sibling of the approver `App`, not
 * an ancestor — nothing here can re-anchor the approver's fixed confetti canvas
 * (the containing-block rule in demoReveal.ts only concerns ancestors of it).
 *
 * MOTION
 * ------
 * Uses the demo's proven idiom — CSS transitions for the gliding dim/ring and
 * `tw-animate-css` for the caption, both behind Tailwind's `motion-safe:`
 * variant — so under `prefers-reduced-motion: reduce` every element paints in
 * its final state with no animation at all. (Deliberately not `motion/react`
 * here: an AnimatePresence enter tween stalled under the demo's re-renders; the
 * spring-driven work lives in the approve fly, which is a one-shot.)
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { demoStrings } from './demoStrings'

export const SEEN_KEY = 'sked-demo-tour-seen-v1'

/**
 * Fired on `document` when the tour closes (finish or skip), so the lingering
 * approve hint knows a first-visit tour has just cleared and can take over.
 */
export const TOUR_CLOSED_EVENT = 'sked-demo-tour-closed'

/** Let the load reveal settle before the tour takes over. */
const START_DELAY_MS = 850

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

const readRect = (selector: string): Rect | null => {
  const el = document.querySelector(selector)
  if (!el) {
    return null
  }
  const r = el.getBoundingClientRect()
  return { top: r.top, left: r.left, width: r.width, height: r.height }
}

const rectStyle = (rect: Rect): React.CSSProperties => ({
  top: rect.top,
  left: rect.left,
  width: rect.width,
  height: rect.height,
})

interface TourStep {
  /** The pane the caption is about (kept bright + ringed). */
  target: string
  /** The pane dimmed to throw focus onto the target. */
  dim: string
  /** Which side this step spotlights — drives the ring + pill accent colour. */
  side: 'client' | 'team'
  /** The bold pill at the top of the spotlighted pane — the step's label. */
  pill: string
  caption: string
  /** One supporting sentence under the caption. */
  body: string
}

const STEPS: TourStep[] = [
  {
    target: '[data-tour="client"]',
    dim: '[data-tour="team"]',
    side: 'client',
    pill: demoStrings.tourClientPill,
    caption: demoStrings.tourClientCaption,
    body: demoStrings.tourClientBody,
  },
  {
    target: '[data-tour="team"]',
    dim: '[data-tour="client"]',
    side: 'team',
    pill: demoStrings.tourTeamPill,
    caption: demoStrings.tourTeamCaption,
    body: demoStrings.tourTeamBody,
  },
]

/** The two side accents, driven from the step. Spice = client, blurple = team. */
const RING_CLASS: Record<TourStep['side'], string> = {
  client: 'shadow-[0_0_0_3px_rgba(255,133,44,0.95)]',
  team: 'shadow-[0_0_0_3px_rgba(79,49,255,0.95)]',
}

const PILL_CLASS: Record<TourStep['side'], string> = {
  client: 'bg-spice-500',
  team: 'bg-blue-500',
}

const DemoTour: React.FC = () => {
  const [active, setActive] = useState(false)
  const [step, setStep] = useState(0)
  const [targetRect, setTargetRect] = useState<Rect | null>(null)
  const [dimRect, setDimRect] = useState<Rect | null>(null)

  // The two focusable controls in the caption. Focus is moved to `advanceRef`
  // on open and on every step change, and Tab is trapped between the two.
  const advanceRef = useRef<HTMLButtonElement>(null)
  const skipRef = useRef<HTMLButtonElement>(null)

  // Auto-play once, after the entrance reveal has settled.
  useEffect(() => {
    let seen = false
    try {
      seen = window.localStorage.getItem(SEEN_KEY) === '1'
    } catch {
      seen = false
    }
    if (seen) {
      return
    }
    const id = window.setTimeout(() => setActive(true), START_DELAY_MS)
    return () => window.clearTimeout(id)
  }, [])

  const current = STEPS[step]

  // Measure the two panes for this step, and keep them correct on resize.
  useLayoutEffect(() => {
    if (!active) {
      return
    }
    const measure = () => {
      setTargetRect(readRect(current.target))
      setDimRect(readRect(current.dim))
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [active, current.target, current.dim])

  const finish = useCallback(() => {
    setActive(false)
    try {
      window.localStorage.setItem(SEEN_KEY, '1')
    } catch {
      /* private mode — the tour simply plays again next time */
    }
    // Let the lingering approve hint know a first-visit tour has cleared.
    document.dispatchEvent(new CustomEvent(TOUR_CLOSED_EVENT))
  }, [])

  // Whether the caption is actually in the DOM (below we render nothing until a
  // rect has been measured). Focus/trap keys off this, not `active` alone, so
  // the move lands on the button once it exists.
  const isOpen = active && targetRect != null

  // Move focus to the advance button on open and on each step change, so the
  // dialog is announced and keyboard users start on the primary action.
  useEffect(() => {
    if (!isOpen) {
      return
    }
    advanceRef.current?.focus()
  }, [isOpen, step])

  // One document-level key handler while the tour is up: Escape closes it
  // (exactly like Skip), and Tab is trapped to cycle between Skip (first) and
  // the advance button (last) only — the dim pane is a click target but is out
  // of the tab order (tabIndex -1), so these two are the whole cycle. The
  // listener lives on `document` rather than on the dialog element because the
  // a11y lint (correctly) rejects key handlers on a non-interactive role, and
  // the aria-modal dialog holds focus regardless.
  useEffect(() => {
    if (!active) {
      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        finish()
        return
      }
      if (event.key === 'Tab') {
        const first = skipRef.current
        const last = advanceRef.current
        if (!first || !last) {
          return
        }
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [active, finish])

  const advance = () =>
    step < STEPS.length - 1 ? setStep((s) => s + 1) : finish()

  if (!active || !targetRect) {
    return null
  }

  const isLast = step === STEPS.length - 1

  return createPortal(
    <div
      className="fixed inset-0 z-[60]"
      role="dialog"
      aria-modal="true"
      aria-label="How this demo works"
      aria-describedby="demo-tour-caption demo-tour-body"
    >
      {/* Subtle vignette: darkens the screen edges so the eye settles on the
          bright spotlighted pane. Purely decorative and click-through — the
          transparent hit target below still absorbs the click. Opacity-only, so
          it simply appears under reduced motion (the fade is motion-safe). */}
      <div
        aria-hidden="true"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 42%, rgba(15,23,42,0.34) 100%)',
        }}
        className="pointer-events-none fixed inset-0 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-500"
      />

      {/* Explicit hit target under everything else, so a click anywhere on the
          overlay — including over the bright, un-dimmed pane — is absorbed here
          and never reaches the approver UI beneath. Transparent, no navigation;
          the dim button and caption sit above it and stay interactive. */}
      <div aria-hidden="true" className="absolute inset-0" />

      {/* Dim the other pane to throw focus onto the target. Clicking it (or the
          caption's button) advances. Opacity lives in the background colour so
          the element itself stays fully opaque. Out of the tab order — the
          caption's Next button is the keyboard path. */}
      {dimRect && (
        <button
          type="button"
          tabIndex={-1}
          aria-label="Continue the intro"
          onClick={advance}
          style={rectStyle(dimRect)}
          className="absolute cursor-pointer rounded-xl bg-slate-900/55 motion-safe:transition-all motion-safe:duration-500 motion-safe:ease-out"
        />
      )}

      {/* Ring the active pane; glides between panes on step change. Its colour
          is side-driven — spice on the client step, blurple on the team step. */}
      <div
        aria-hidden="true"
        style={rectStyle(targetRect)}
        className={`pointer-events-none absolute rounded-xl ${RING_CLASS[current.side]} motion-safe:transition-all motion-safe:duration-500 motion-safe:ease-out`}
      />

      {/* Bold pill at the top-centre of the spotlighted pane — the step's label.
          Amber for the client side, blurple for the team side. */}
      <div
        key={`pill-${step}`}
        aria-hidden="true"
        style={{
          left: targetRect.left + targetRect.width / 2,
          top: targetRect.top + 14,
          transform: 'translateX(-50%)',
        }}
        className={`pointer-events-none absolute rounded-full px-3.5 py-1 font-gotham text-xs font-extrabold uppercase tracking-[0.14em] text-white shadow-lg ${PILL_CLASS[current.side]} motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-300`}
      >
        {current.pill}
      </div>

      {/* Caption, centred over the active pane. Keyed by step so it re-animates
          when the copy changes. */}
      <div
        key={step}
        style={{
          left: targetRect.left + targetRect.width / 2,
          top: targetRect.top + Math.min(targetRect.height * 0.42, 300),
          transform: 'translate(-50%, -50%)',
        }}
        className="pointer-events-auto absolute w-[19rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-neutral-200 bg-white p-5 text-center shadow-2xl motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-300"
      >
        <p
          id="demo-tour-caption"
          className="font-gotham text-xl font-extrabold leading-snug text-neutral-800"
        >
          {current.caption}
        </p>
        <p
          id="demo-tour-body"
          className="mt-2 text-sm font-medium leading-snug text-neutral-500"
        >
          {current.body}
        </p>
        <div className="mt-4 flex items-center justify-between">
          <button
            ref={skipRef}
            type="button"
            onClick={finish}
            aria-label="Skip the intro"
            className="text-sm font-medium text-neutral-400 motion-safe:transition-colors hover:text-neutral-600"
          >
            Skip
          </button>
          <span className="text-xs font-medium text-neutral-400">
            {step + 1} / {STEPS.length}
          </span>
          <button
            ref={advanceRef}
            type="button"
            onClick={advance}
            className="rounded-full bg-blue-500 px-4 py-2 text-sm font-semibold text-white shadow-[0_3px_10px_rgba(0,0,0,.2)] motion-safe:transition-colors hover:bg-blue-600"
          >
            {isLast ? 'Approve one →' : 'Next'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default DemoTour
